/**
 * The platform console (/admin/staff) — everyone CWY employs.
 *
 * Super-admin only. Two genuinely different kinds of person share this screen,
 * because "who works here" is one question:
 *
 *  - an Admin is a platform operator. They create and suspend schools and hold no
 *    membership anywhere, which is the whole authorization model: managing tenants
 *    is not an action inside one, so an operator cannot read a school's students.
 *  - a Teacher is CWY's OWN teaching staff — a membership in the first-party
 *    workspace, teaching the direct-to-consumer students. They have no platform
 *    authority at all.
 *
 * This screen is the only way to create the second kind. Before it existed a
 * first-party teacher could only be produced by the seed script writing the
 * membership row directly: creating one needs `create:staff`, which comes from a
 * SCHOOL_ADMIN membership in the first-party tenant, and nobody holds one.
 *
 * The super admin appears here read-only. It is reconciled from the environment on
 * every API startup, so disabling it from a UI would be undone by the next restart
 * — and could lock out the only account able to undo it in the meantime.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  MEMBERSHIP_ROLE_LABEL,
  PLATFORM_ROLE_LABEL,
  type MembershipRole,
  type PlatformRole,
  type PlatformStaffRole,
} from '@cwy/config/contracts';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  StudentPage,
  StudentPageHeader,
} from '@/components/ds';
import { SecretReveal } from '@/components/admin/SecretReveal';
import { QueryState } from '@/components/admin/QueryState';

const STAFF_KEY = ['platform', 'staff'] as const;

interface StaffRow {
  kind: 'PLATFORM' | 'TENANT';
  userId: string;
  membershipId: string | null;
  name: string;
  email: string;
  role: PlatformRole | MembershipRole;
  isBootstrapAdmin: boolean;
  enabled: boolean;
  subjects: string[];
  createdAt: string;
}

interface CreatedStaff {
  email: string;
  /** Null when an existing CWY account was attached rather than created. */
  temporaryPassword: string | null;
}

/** What each role can actually do, said plainly on the form. */
const ROLE_BLURB: Record<PlatformStaffRole, string> = {
  PLATFORM_ADMIN:
    'Creates and suspends schools, and provisions a school’s first admin. Cannot read any school’s students, papers or reports.',
  TEACHER:
    'Teaches CWY’s own students at the root domain. Signs in at /staff and works in the teacher console.',
};

const ROLE_OPTION_LABEL: Record<PlatformStaffRole, string> = {
  PLATFORM_ADMIN: 'Admin — manages schools',
  TEACHER: 'Teacher — teaches CWY students',
};

function roleLabelOf(row: StaffRow): string {
  return row.kind === 'PLATFORM'
    ? PLATFORM_ROLE_LABEL[row.role as PlatformRole]
    : MEMBERSHIP_ROLE_LABEL[row.role as MembershipRole];
}

export function PlatformStaffPage() {
  const staff = useQuery({
    queryKey: STAFF_KEY,
    queryFn: () => apiGet<StaffRow[]>('/platform/staff'),
  });

  const [showForm, setShowForm] = useState(false);
  const [role, setRole] = useState<PlatformStaffRole>('TEACHER');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [created, setCreated] = useState<CreatedStaff | null>(null);

  const addStaff = useMutation({
    mutationFn: () =>
      apiPost<CreatedStaff>('/platform/staff', { name: name.trim(), email: email.trim(), role }),
    onSuccess: (result) => {
      setCreated(result);
      setShowForm(false);
      setName('');
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: STAFF_KEY });
    },
    onError: (err: Error) => setError(err.message),
  });

  const setEnabled = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      apiPatch(`/platform/staff/${userId}/status`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STAFF_KEY }),
    onError: (err: Error) => setError(err.message),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (name.trim() && email.trim()) addStaff.mutate();
  };

  return (
    <StudentPage>
      <StudentPageHeader
        title="Staff"
        subtitle="CWY's own people — operators who manage schools, and the teachers who teach our direct students."
        action={
          <Button onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'Add staff'}
          </Button>
        }
      />

      {created && (
        <SecretReveal
          title={
            created.temporaryPassword
              ? 'Added — give them these details'
              : 'Added to an existing CWY account'
          }
          identifier={created.email}
          // An attached account already has a password this console has no
          // authority to replace, so there is nothing to hand over.
          secret={created.temporaryPassword ?? '(existing password unchanged)'}
          onDismiss={() => setCreated(null)}
        />
      )}

      {error && <p className="text-sm text-danger">{error}</p>}

      {showForm && (
        <Card title="Add staff">
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <Field label="Role" required>
              {({ id }) => (
                <Select
                  id={id}
                  value={role}
                  onChange={(e) => setRole(e.target.value as PlatformStaffRole)}
                >
                  {(Object.keys(ROLE_OPTION_LABEL) as PlatformStaffRole[]).map((option) => (
                    <option key={option} value={option}>
                      {ROLE_OPTION_LABEL[option]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="flex flex-wrap gap-3">
              <Field label="Full name" className="min-w-[200px] flex-1" required>
                {({ id }) => (
                  <Input
                    id={id}
                    autoFocus
                    value={name}
                    maxLength={120}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Work email" className="min-w-[240px] flex-1" required>
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    value={email}
                    maxLength={255}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <p className="text-xs text-ink-soft">{ROLE_BLURB[role]}</p>
            {role !== 'PLATFORM_ADMIN' && (
              <p className="text-xs text-ink-soft">
                They sign in at <span className="font-semibold">/staff</span> with a temporary
                password shown once here, and must change it before anything else. Assign their
                subjects afterwards on each classroom they take.
              </p>
            )}
            <div>
              <Button type="submit" disabled={addStaff.isPending}>
                {addStaff.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <QueryState
        isPending={staff.isPending}
        error={staff.error}
        isEmpty={staff.data?.length === 0}
        empty={<p className="text-sm text-ink-soft">Nobody here yet.</p>}
      >
        <div className="flex flex-col gap-2.5">
          {staff.data?.map((row) => (
            <Card key={`${row.kind}:${row.userId}`} padding="13px 16px">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-bold text-ink">{row.name}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-fs-11 font-semibold text-ink-soft">
                      {roleLabelOf(row)}
                    </span>
                    {row.isBootstrapAdmin && (
                      <span
                        className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                        style={{ background: 'var(--brand-lime)', color: 'var(--brand-forest)' }}
                        title="Reconciled from the deployment environment on every API startup."
                      >
                        From environment
                      </span>
                    )}
                    {!row.enabled && (
                      <span
                        className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                        style={{ background: 'var(--bin-pink-bg)', color: 'var(--bin-pink-deep)' }}
                      >
                        Disabled
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-ink-soft">
                    {row.email}
                    {row.subjects.length > 0 && ` · ${row.subjects.join(', ')}`}
                    {row.kind === 'TENANT' && row.subjects.length === 0 && ' · no classes yet'}
                  </p>
                </div>

                {row.isBootstrapAdmin ? (
                  <span className="text-xs text-ink-faint">
                    Rotate this password in the environment
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={setEnabled.isPending}
                    onClick={() => {
                      const next = !row.enabled;
                      const message = next
                        ? `Re-enable ${row.name}?`
                        : `Disable ${row.name}? They are signed out immediately.`;
                      if (window.confirm(message)) {
                        setError('');
                        setEnabled.mutate({ userId: row.userId, enabled: next });
                      }
                    }}
                  >
                    {row.enabled ? 'Disable' : 'Re-enable'}
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      </QueryState>
    </StudentPage>
  );
}
